import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { getInstance } from "../auth";

import PolygonsStore from "@/store/api_stores/polygons";
import UserStore from "@/store/api_stores/users";
import MachineLearningStore from "@/store/api_stores/ml";

Vue.use(Vuex);

let isDevelopmentMode = process.env.NODE_ENV === "development";

const DEBUG = false; // console.log(...) for request/response
const log = (...args) => {
  if (DEBUG) {
    console.log(...args);
  }
};

axios.defaults.paramsSerializer = function(params) {
  let resultItems = Object.keys(params).map(
    (key) => `${key}=${encodeURIComponent(params[key])}`
  );
  return resultItems.join("&");
};

const state = {
  sidebarShow: "responsive",
  sidebarMinimize: isDevelopmentMode,
  authClient: null,
  apiToken: null,
  user: null,
  idToken: null,
  activeRequestsCount: 0,
  auth0ManagementToken: null,
  roles: [{ label: "Select Role", value: null, disabled: true }],
};

const getters = {
  apiRequestHeaders(state) {
    // sets auth header if apiToken is available
    let headers = {
      // cache-controls is denied in cors currently
      // 'Cache-Control': 'no-cache',
    };
    if (state.apiToken) {
      headers["Authorization"] = `Bearer ${state.apiToken}`;
    }
    return headers;
  },
  auth0RequestHeaders(state) {
    // sets auth header if apiToken is available
    let headers = {
      // cache-controls is denied in cors currently
      // 'Cache-Control': 'no-cache',
    };
    if (state.auth0ManagementToken) {
      headers["Authorization"] = `Bearer ${state.auth0ManagementToken}`;
    }
    return headers;
  },
};

function expiredToken(token) {
  let decoded = jwt_decode(token);
  let expired = decoded.exp * 1000 < Date.now() - 10000;
  return expired;
}

const mutations = {
  toggleSidebarDesktop(state) {
    const sidebarOpened = [true, "responsive"].includes(state.sidebarShow);
    state.sidebarShow = sidebarOpened ? false : "responsive";
  },
  toggleSidebarMobile(state) {
    const sidebarClosed = [false, "responsive"].includes(state.sidebarShow);
    state.sidebarShow = sidebarClosed ? true : "responsive";
  },
  set(state, [variable, value]) {
    state[variable] = value;
  },
  setFiltersSchema(state, value) {
    state.filtersSchema = value;
  },
  setAuthClient(state, value) {
    state.authClient = value;
  },
  setAPIToken(state, value) {
    state.apiToken = value;
  },
  setIdToken(state, value) {
    state.idToken = value;
  },
  setAuth0Token(state, value) {
    state.auth0ManagementToken = value;
  },
  setUser(state, value) {
    state.user = value;
  },
  setActiveRequestsCount(state, value) {
    state.activeRequestsCount = value;
  },
};

const actions = {
  async getAuthClient({ commit, state }) {
    if (state.authClient) {
      return state.authClient;
    }

    const authService = getInstance();

    if (authService.loading) {
      // it seems it's an impossible case here, it's left just in case to ensure
      // this case it's impossible to return auth0Client from this action
      console.error("auth service is not loaded yet");

      authService.$watch("loading", () => {
        commit("setAuthClient", authService.auth0Client);
      });
    } else {
      commit("setAuthClient", authService.auth0Client);
    }

    return authService.auth0Client;
  },

  async getToken({ state, commit, dispatch }, o) {
    if (state.apiToken && !expiredToken(state.apiToken)) {
      return state.apiToken;
    }
    const authClient = await dispatch("getAuthClient");
    const apiToken = await authClient.getTokenSilently(o);
    commit("setAPIToken", apiToken);

    //console.log('TOKEN IS RETRIEVED SILENTLY');
    return apiToken;
  },

  async getIdToken({ state, commit, dispatch }, o) {
    if (state.idToken) {
      return state.idToken;
    }
    const authClient = await dispatch("getAuthClient");
    const idToken = await authClient.getIdTokenClaims(o);
    commit("setIdToken", idToken);

    //console.log('ID TOKEN IS RETRIEVED SILENTLY');
    return idToken;
  },

  async parsePermissionsFromToken() {
    let token = await getToken();
    var decoded = jwt_decode(token);
    return decoded.scope;
  },

  async getAuth0Token({ state, commit, dispatch }) {
    if (
      state.auth0ManagementToken &&
      !expiredToken(state.auth0ManagementToken)
    ) {
      return state.auth0ManagementToken;
    }
    /*
    var apiToken = ""
    if(state.apiToken) {
      apiToken = state.apiToken;
    } else {
      apiToken = await dispatch('getToken');
    }*/
    const resp = await dispatch("getAuth0TokenFromGCF");
    commit("setAuth0Token", resp.access_token);

    //console.log('TOKEN IS RETRIEVED SILENTLY');
    return resp.access_token;
  },

  async getAuth0TokenFromGCF({ dispatch }) {
    let response = await dispatch("apiRequestGCFTokenExchange", {
      method: "get",
    });

    if (response.error) {
      console.log("api error", response);
      return null;
    }

    return response.data;
  },
  async getLoggedInUser({ commit, state, dispatch }) {
    if (state.user) {
      return state.user;
    }

    const authClient = await dispatch("getAuthClient");
    const user = await authClient.getUser(); // todo: handle null
    commit("setUser", user);
    return user;
  },
  async logout({ dispatch }) {
    const authClient = await dispatch("getAuthClient");
    await authClient.logout({ returnTo: window.location.origin });
  },

  async apiRequest(
    { getters, dispatch, commit },
    { baseUrl, method, path, data, headers }
  ) {
    log("apiRequest", method, path, JSON.stringify(data, null, 2));

    // common method to get/send data to the API
    const dataMethods = ["post", "put"]; // data will be sent in a request body
    const paramsMethods = ["get"]; // data will be sent as get parameters

    // available as state.apiToken in common situations,
    // otherwise it will be retrieved silently
    let token = await dispatch("getToken");
    if (!token) {
      console.error("unable to retrieve a token!");
      return;
    }

    let requestData = {
      headers: { ...getters.apiRequestHeaders, ...(headers || {}) },
      method: method,
      url: `${baseUrl || process.env.VUE_APP_API_URL}${path}`,
      data: dataMethods.includes(method.toLowerCase()) ? data : null,
      params: paramsMethods.includes(method.toLowerCase()) ? data : null,
    };

    try {
      commit("setActiveRequestsCount", getters.getActiveRequestsCount + 1);
      let result = await axios(requestData);
      commit("setActiveRequestsCount", getters.getActiveRequestsCount - 1);

      log(`api result ${path}`, result);
      return {
        error: false,
        status_code: result.status,
        data: result.data,
        requestData: requestData,
      };
    } catch (error) {
      commit("setActiveRequestsCount", getters.getActiveRequestsCount - 1);
      log(`api error ${path}`, error);
      if (error.response) {
        // http errors: 404, 403, 500, etc
        return {
          error: true,
          status_code: error.response.status,
          error_details: error.response.data,
          requestData: requestData,
        };
      } else {
        // network error for example
        return {
          error: true,
          error_details: [error.message],
          requestData: requestData,
        };
      }
    }
  },

  // async apiRequestPolygons({getters, dispatch}, {method, path, data, headers}) {
  //   // console.log('apiRequest', method, apiUrl, data);
  //
  //   // common method to get/send data to the API
  //   const dataMethods = ['post', 'put','patch'];  // data will be sent in a request body
  //   const paramsMethods = ['get','delete'];  // data will be sent as get parameters
  //
  //   // available as state.apiToken in common situations,
  //   // otherwise it will be retrieved silently
  //   let token = await dispatch('getToken');
  //   if(!token) {
  //     console.error('unable to retrieve a token!');
  //     return;
  //   }
  //
  //   let requestData = {
  //     headers: {...getters.apiRequestHeaders, ...headers || {}},
  //     method: method,
  //     url: `https://beta.api.staging.internal.oceanbolt.com/v1/polygons${path}`,
  //     data:  dataMethods.includes(method) ? data : null,
  //     params: paramsMethods.includes(method) ? data : null,
  //   };
  //
  //   try {
  //
  //     let result = await axios(requestData);
  //
  //     // console.log(`api result ${apiUrl}`, result);
  //     return {
  //       error: false,
  //       status_code: result.status,
  //       data: result.data,
  //       requestData: requestData,
  //     };
  //   } catch (error) {
  //     // console.log(`api error ${apiUrl}`, error);
  //     if(error.response) {
  //       // http errors: 404, 403, 500, etc
  //       return {
  //         error: true,
  //         status_code: error.response.status,
  //         error_details: error.response.data,
  //         requestData: requestData,
  //       };
  //     } else {
  //       // network error for example
  //       return {
  //         error: true,
  //         error_details: [error.message],
  //         requestData: requestData,
  //       }
  //     }
  //   }
  // },

  async apiRequestGCFTokenExchange(
    { getters, dispatch },
    { method, data, headers }
  ) {
    // console.log('apiRequest', method, apiUrl, data);

    // common method to get/send data to the API
    const dataMethods = ["post", "put"]; // data will be sent in a request body
    const paramsMethods = ["get"]; // data will be sent as get parameters

    // available as state.apiToken in common situations,
    // otherwise it will be retrieved silently
    let token = await dispatch("getToken");
    if (!token) {
      console.error("unable to retrieve a token!");
      return;
    }

    let requestData = {
      headers: { ...getters.apiRequestHeaders, ...(headers || {}) },
      method: method,
      url: `${process.env.VUE_APP_GCF_API_URL}`,
      data: dataMethods.includes(method) ? data : null,
      params: paramsMethods.includes(method) ? data : null,
    };

    try {
      let result = await axios(requestData);

      // console.log(`api result ${apiUrl}`, result);
      return {
        error: false,
        status_code: result.status,
        data: result.data,
        requestData: requestData,
      };
    } catch (error) {
      // console.log(`api error ${apiUrl}`, error);
      if (error.response) {
        // http errors: 404, 403, 500, etc
        return {
          error: true,
          status_code: error.response.status,
          error_details: error.response.data,
          requestData: requestData,
        };
      } else {
        // network error for example
        return {
          error: true,
          error_details: [error.message],
          requestData: requestData,
        };
      }
    }
  },
  async apiRequestUserManagementAuth0(
    { getters, dispatch },
    { method, apiUrl, data, headers }
  ) {
    // console.log('apiRequest', method, apiUrl, data);

    // common method to get/send data to the API
    const dataMethods = ["post", "put", "patch"]; // data will be sent in a request body
    const paramsMethods = ["get"]; // data will be sent as get parameters

    // available as state.apiToken in common situations,
    // otherwise it will be retrieved silently
    let auth0token = await dispatch("getAuth0Token");
    if (!auth0token) {
      console.error("unable to retrieve a token!");
      return;
    }

    let requestData = {
      headers: { ...getters.auth0RequestHeaders, ...(headers || {}) },
      method: method,
      url: `${process.env.VUE_APP_AUTH0_API_URL}${apiUrl}`,
      data: dataMethods.includes(method) ? data : null,
      params: paramsMethods.includes(method) ? data : null,
    };

    try {
      let result = await axios(requestData);

      // console.log(`api result ${apiUrl}`, result);
      return {
        error: false,
        status_code: result.status,
        data: result.data,
        requestData: requestData,
      };
    } catch (error) {
      // console.log(`api error ${apiUrl}`, error);
      if (error.response) {
        // http errors: 404, 403, 500, etc
        return {
          error: true,
          status_code: error.response.status,
          error_details: error.response.data,
          requestData: requestData,
        };
      } else {
        // network error for example
        return {
          error: true,
          error_details: [error.message],
          requestData: requestData,
        };
      }
    }
  },

  // async listPorts ({dispatch}, parameters) {
  //   let response = await dispatch(
  //     'apiRequestPolygons',
  //     {
  //       method: 'get',
  //       path: '/ports',
  //       data: parameters,
  //     }
  //   );
  //
  //   return response.data;
  // },

  async createUser({ dispatch }, parameters) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "post",
      apiUrl: "/users",
      data: parameters,
    });

    return response;
  },

  async updateUser({ dispatch }, payload) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "patch",
      apiUrl: `/users/${payload.id}`,
      data: payload.data,
    });
    return response;
  },

  async deleteUserFunction({ getters, dispatch }, user_id) {
    let auth0token = await dispatch("getAuth0Token");
    if (!auth0token) {
      console.error("unable to retrieve a token!");
      return;
    }
    let requestData = {
      headers: { ...getters.auth0RequestHeaders },
      method: "delete",
      url: `${process.env.VUE_APP_AUTH0_API_URL}/users/${user_id}`,
      data: {},
      params: {},
    };

    try {
      let result = await axios(requestData);
      return {
        error: false,
        status_code: result.status,
        data: result.data,
        requestData: requestData,
      };
    } catch (error) {
      if (error.response) {
        // http errors: 404, 403, 500, etc
        return {
          error: true,
          status_code: error.response.status,
          error_details: error.response.data,
          requestData: requestData,
        };
      } else {
        // network error for example
        return {
          error: true,
          error_details: [error.message],
          requestData: requestData,
        };
      }
    }
  },

  async deleteUserRole({ getters, dispatch }, payload) {
    let auth0token = await dispatch("getAuth0Token");
    if (!auth0token) {
      console.error("unable to retrieve a token!");
      return;
    }

    let requestData = {
      headers: { ...getters.auth0RequestHeaders },
      method: "delete",
      url: `${process.env.VUE_APP_AUTH0_API_URL}/users/${payload.user_id}/roles`,
      data: { roles: [payload.role_id] },
      params: {},
    };
    try {
      let result = await axios(requestData);
      return {
        error: false,
        status_code: result.status,
        data: result.data,
        requestData: requestData,
      };
    } catch (error) {
      if (error.response) {
        // http errors: 404, 403, 500, etc
        return {
          error: true,
          status_code: error.response.status,
          error_details: error.response.data,
          requestData: requestData,
        };
      } else {
        // network error for example
        return {
          error: true,
          error_details: [error.message],
          requestData: requestData,
        };
      }
    }
  },

  async listUsers({ dispatch }, params) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/users`,
      data: params,
    });

    if (response.error) {
      console.log("api error", response);
      return null;
    }

    return response.data;
  },

  async getUserAuth0({ dispatch }, user_id) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/users/` + user_id,
      data: {},
    });

    if (response.error) {
      console.log("api error", response);
      return null;
    }

    return response.data;
  },

  async getUserRole({ dispatch }, user_id) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/users/${user_id}/roles`,
      data: {},
    });
    return response;
  },

  async getRoles({ state, dispatch }) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: "/roles",
      data: {},
    });
    if (response.error) {
      return null;
    }
    state.roles = [{ label: "-- Select Role --", value: null, disabled: false }];
    response.data.forEach((role) => {
      role.label = role.name;
      role.value = role.id;
      state.roles.push(role);
    });
    return response.data;
  },

  async getRolesAsList({ state, dispatch }) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: "/roles",
      data: {},
    });
    if (response.error) {
      return null;
    }
    response.data.forEach((role) => {
      role.label = role.name;
      role.value = role.id;
    });
    return response.data;
  },

  async addRoleToUser({ dispatch }, payload) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "post",
      apiUrl: `/roles/${payload.roleId}/users`,
      data: {
        users: [payload.id],
      },
    });
    if (response.error) {
      return null;
    }
    return response.data;
  },

  async addRole({ dispatch }, payload) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "post",
      apiUrl: `/users/${payload.id}/roles`,
      data: {
        roles: payload.roles,
      },
    });
    return response;
  },

  async getRoleUsers({ dispatch }, payload) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/roles/${payload.id}/users`,
      data: payload.params,
    });

    return response;
  },

  async getRoleById({ dispatch }, id) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/roles/${id}`,
      data: {},
    });
    return response;
  },

  async getRolePermissionsById({ dispatch }, id) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/roles/${id}/permissions`,
      data: {},
    });
    return response;
  },

  async getUserPermissionsById({ dispatch }, id) {
    let response = await dispatch("apiRequestUserManagementAuth0", {
      method: "get",
      apiUrl: `/users/${id}/permissions`,
      data: {},
    });
    return response;
  },
};

export default new Vuex.Store({
  modules: {
    polygons: PolygonsStore,
    ml: MachineLearningStore,
    users: UserStore,
  },
  state,
  mutations,
  actions,
  getters,
});
